import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { ConfirmationService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { ChildEvent, ScrBaseComponent } from 'src/app/components/base.component';
import { Account } from 'src/app/models/account.model';
import { User } from 'src/app/models/user.model';
import { AccountService } from 'src/app/services/account.service';
import { ScrAuthorizationService } from 'src/app/services/authorization.service';
import { ConfigService, EmailCategory, UserRole } from 'src/app/services/config.service';
import { UserService } from 'src/app/services/user.service';
import { Message } from 'src/app/shared/messages/messages.component';
import { handleEnterKeyPress } from 'src/app/shared/common-events';
import { CacheService, ScrAuthenticationService } from 'src/app/services';
import { CACHEKEY_USER } from 'src/app/shared/constants';
import { ShortCodesRole, ShortCodesRoleLabels } from '../../../../../models/constants';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss']
})
export class UserComponent extends ScrBaseComponent implements OnInit {

  @Input() mode!: string;
  @Input() user?: User;
  @ViewChild('userForm', { static: false }) userForm!: NgForm;

  @Output() emitAction = new EventEmitter<ChildEvent>();
  @ViewChild(NgxMaskDirective) maskDir: NgxMaskDirective | undefined;
  account: Account = {} as Account;
  userProfile: User = {} as User;
  emailCategories: EmailCategory[] = [];
  userRoles: UserRole[] = [];
  public publicEmailDomains: string[] = [];
  private myUserName?: string | null;

  loading = false;
  submitted = false;
  displayForm = false;

  constructor(private configService: ConfigService,
    private userService: UserService,
    private accountService: AccountService,
    private cacheService: CacheService,
    private authenticationService: ScrAuthenticationService,
    private authorizationService: ScrAuthorizationService,
    private confirmService: ConfirmationService) {
    super();

    this.myUserName = this.authenticationService.getScrUserName();
  }

  ngOnInit(): void {

    this.userProfile.roleList = [];
    this.userProfile.emailCategoryList = [];
    this.userRoles = this.configService.getRoles();
    this.configService.getConfig().subscribe(cfg => {
      this.publicEmailDomains = cfg.publicEmailDomains;
    });

    this.authorizationService.getAuthorizedGroups().subscribe(grps => {

      forkJoin([this.configService.getEmailCategories(), this.accountService.getAccount()]).subscribe(rsp => {
        this.displayForm = true;

        this.emailCategories = (rsp[0] as EmailCategory[]).filter(cat => {
          if ((cat.emailCategoryId === 6 || cat.emailCategoryId === 7) && (grps.includes(ShortCodesRole.CarrierRegistrant) || (grps.includes(ShortCodesRole.Carrier)))) {
            return false;
          }
          return true;
        });

        if (grps.includes(ShortCodesRole.CarrierRegistrant) || grps.includes(ShortCodesRole.Carrier)) {
          this.userRoles = this.configService.getCarrierRoles();
        }
        
        this.account = rsp[1].account;

        if (this.mode === 'update' && this.user) {
          this.userProfile = JSON.parse(JSON.stringify(this.user));
          if (this.userProfile.emailCategoryList === undefined) {
            this.userProfile.emailCategoryList = [];
          }
          if (this.userProfile.roleList.includes(ShortCodesRole.API)) {
            this.userRoles.splice(2, 0, { label: ShortCodesRoleLabels.API, value: ShortCodesRole.API, disabled: true });
          }
          if (this.userProfile.roleList.includes(ShortCodesRole.VettingMonitoring)) {
            this.userRoles.splice(2, 0, { label: ShortCodesRoleLabels.VettingMonitoring, value: ShortCodesRole.VettingMonitoring, disabled: true });
          }
          if (this.userProfile.roleList.includes(ShortCodesRole.BIReports)) {
            this.userRoles.splice(2, 0, { label: ShortCodesRoleLabels.BIReports, value: ShortCodesRole.BIReports, disabled: true });
          }
          this.userProfile.confirmEmail = '';
        }
        else {
          this.userProfile.country = 'us';
        }
        this.userProfile.country = this.userProfile.country?.toLowerCase();
        if (this.mode === 'add') {
          this.userProfile.country = this.account.country?.toLowerCase() ?? '';
          this.userProfile.address1 = this.account.address1 ?? '';
          this.userProfile.address2 = this.account.address2;
          if (this.account.city !== undefined) {
            this.userProfile.city = this.account.city;
          }
          if (this.account.state !== undefined) {
            this.userProfile.state = this.account.state;
          }
          if (this.account.zip !== undefined) {
            this.userProfile.zip = this.account.zip;
          }
        }
        if (this.account.company !== undefined) {
          this.userProfile.company = this.account.company;
        }

      }, (error: HttpErrorResponse) => { this.processHttpError(error); this.jumpToTop(); });
    });
  }

  submit() {
    this.clearMessages();
    this.submitted = true;

    const msgs: Message[] = [];

    this.validateRoles(msgs);

    if (msgs.length !== 0) {
      msgs.forEach(m => {
        if (m.elementId) {
          if (!this.userForm.form.get(m.elementId)?.errors?.required) {
            this.userForm.form.get(m.elementId)?.setErrors({ invalid: m.text });
          }
        }

        if (m.elementId === 'phone' && !this.userForm.form.get(m.elementId)?.errors?.required) {
          this.userForm.form.get(m.elementId)?.setErrors({ invalid: "Value for Phone Number must be between 7 and 16 characters." });

        }
      });
      for (const key of Object.keys(this.userForm.controls)) {
        if (this.userForm.control.get(key)?.errors) {
          const elmt = document.getElementById(key);
          elmt?.scrollIntoView({ behavior: "smooth" });
          return;
        }
      }
      return;
    }
    if ((this.userProfile.email !== this.userProfile.confirmEmail) && this.userForm?.controls.email.dirty) {
      this.clearMessages();
      this.displayLocalizedError('scr.emailmismatch', undefined, 'message');
    } else if (this.userForm?.valid) {
      if (!this.userProfile.userName) {
        this.userProfile.userName = null;
      }

      const userProfile = { ...this.userProfile };
      delete userProfile.confirmEmail;

      const formData = Object.assign({} as User, JSON.parse(JSON.stringify(userProfile), this.prepareForSubmit));

      if (this.mode === 'update') {

        this.observe(this.userService.editUserProfile(formData), rsp => {
          this.userForm.reset();
          this.userProfile.confirmEmail = '';
          this.emitAction.emit({ action: 'reload', value: 'reload', message: this.getFirstMessage(rsp)});

          if (this.myUserName === this.userProfile.userName) {
            // invalidate cache.
            this.cacheService.invalidate(CACHEKEY_USER);
          }
          return false;
        });
      }
      else {
        this.observe(this.userService.addUserProfile(formData), rsp => {
          this.userForm.reset();
          this.userProfile.confirmEmail = '';
          this.emitAction.emit({ action: 'reload', value: 'reload', message: this.getFirstMessage(rsp) });
          return false;
        });
      }
    }
    else {
      this.scrollToFirstError();
    }
  }

  prepareForSubmit(key: string, value: string|boolean|number) {
    if (key === 'name') {
      return;
    }
    if (typeof value === 'string')
      return value.trim();

    return value;
  }

  checked(choice: string) {
    if (this.userProfile.emailCategoryList !== undefined) {
      if (this.userProfile.emailCategoryList.indexOf(choice) !== -1) {
        return true;
      } else {
        return;
      }
    }
    else return;
  }

  subscriptionsDisabled() {
    return this.userProfile.roleList.length === 0;
  }

  validateRoles(msgs: Message[]): boolean {
    const msg = new Message();
    msg.elementId = "role";
    if (this.userProfile.roleList.includes(ShortCodesRole.API) &&
      !(this.userProfile.roleList.includes(ShortCodesRole.RegistrantAdmin) || this.userProfile.roleList.includes(ShortCodesRole.Registrant))) {
      msg.text = "A user that has been assigned the API role must also be assigned the Registrant or Registrant/Registrant Admin role. Please update the role selections to continue.";
      msgs.push(msg);
      return false;
    }

    if (this.userProfile.roleList.includes(ShortCodesRole.RegistrantAdmin) && !this.userProfile.roleList.includes(ShortCodesRole.Registrant)) {
      msg.text = "A user that has been assigned the Registrant Admin role must also be assigned the Registrant role. Please update the role selections to continue.";
      msgs.push(msg);
      return false;
    }

    if (this.userProfile.roleList.includes(ShortCodesRole.Carrier) && this.userProfile.roleList.includes(ShortCodesRole.CarrierRegistrant)) {
      msg.text = "A user cannot have both Carrier and Carrier Registrant Roles assigned. Remove one of these selected roles to continue.";
      msgs.push(msg);
      return false;
    }

    return true;
  }

  confirmCancelDialog() {
    this.confirmService.confirm({
      key: 'confirmCancel',
      header: 'Confirm Cancellation',
      message: 'Are you sure you want to cancel the request?',
      accept: () => {
        this.userForm.reset();
        this.emitAction.emit({ action: 'cancel', value: 'cancel' });
        this.submitted = false;
      },
      reject: () => {
        this.confirmService.close();
      }
    });
  }

  handleEnter(event : Event){
    handleEnterKeyPress(event);
  }

}
